// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Logger from "@insite/client-framework/Logger";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@west/App/Components/Common/FancyScroll";
import { MiniCartContext } from "@west/App/Components/MiniCart/MiniCartContext";
import { EMPTY_VALUE } from "@west/XDL/constants";
import React, { FC, useContext, useEffect } from "react";

type Props = {
    cartLinesDisplay: (JSX.Element | undefined)[];
};

interface CartLinesHeaderStyles {
    container?: InjectableCss;
    description?: TypographyPresentationProps;
    qty?: TypographyPresentationProps;
    priceUnit?: TypographyPresentationProps;
}

interface MiniCartStyles {
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    cartLinesContainer?: InjectableCss;
    cartLinesHeader?: CartLinesHeaderStyles;
    subTotalContainer?: InjectableCss;
    subTotalLabel?: TypographyPresentationProps;
    subTotalValue?: TypographyPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    actions?: InjectableCss;
    messageWrapper?: InjectableCss;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({ cartLinesDisplay }) => {
    const { isSidebarMenuTippyOpen } = useContext(MiniCartContext);
    const history = useHistory();
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    const { orderSubTotalDisplay, cartPageUrl } = useAppSelector(state => {
        const cartState = getCurrentCartState(state);
        const cart = cartState.value;
        return {
            orderSubTotalDisplay: cart?.orderSubTotalDisplay || EMPTY_VALUE,
            cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        };
    });

    useEffect(() => {
        if (isSidebarMenuTippyOpen && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isSidebarMenuTippyOpen]);

    const handleViewCartClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        history.push(cartPageUrl);
    };

    return (
        <StyledWrapper {...styles.headerContainer} data-test-selector="header-mini-cart">
            <StyledWrapper {...styles.messageWrapper}>
                {createWidgetElement("Common/CartApproverMessage", { fields: {} })}
            </StyledWrapper>
            <StyledWrapper {...styles.cartLinesHeader?.container} data-test-selector="mini-cart-header">
                <Typography {...styles.cartLinesHeader?.description}>{translate("Product Description")}</Typography>
                <Typography {...styles.cartLinesHeader?.qty}>{translate("QTY")}</Typography>
                <Typography {...styles.cartLinesHeader?.priceUnit}>{translate("Price/Unit")}</Typography>
            </StyledWrapper>
            <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                <StyledWrapper
                    {...styles.cartLinesContainer}
                    ref={parentBoxRef}
                    data-test-selector="cartLines-container"
                >
                    {cartLinesDisplay}
                </StyledWrapper>
            </FancyScroll>
            <StyledWrapper {...styles.subTotalContainer} data-test-selector="mini-cart-header">
                <Typography {...styles.subTotalLabel}>{translate("Subtotal")}:</Typography>
                <Typography {...styles.subTotalValue}>{orderSubTotalDisplay}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.actions} data-test-selector="mini-cart-actions">
                <Button {...styles.viewCartButton} onClick={handleViewCartClick} aria-label={translate("View Cart")}>
                    {translate("View Cart")}
                </Button>
                {createWidgetElement("Cart/CartTotal", {
                    fields: {
                        isMiniCart: true,
                    },
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const HeaderMiniCart: FC<{ isSidebarMenuTippyOpen?: boolean }> = ({ isSidebarMenuTippyOpen }) => {
    return (
        <MiniCartContext.Provider value={{ isSidebarMenuTippyOpen }}>
            <StyledWrapper {...styles.container} data-test-selector="mini-cart">
                {createWidgetElement("Cart/CartLines", {
                    fields: {
                        isMiniCart: true,
                    },
                })}
            </StyledWrapper>
        </MiniCartContext.Provider>
    );
};

export default HeaderMiniCart;
export { MiniCart };
